import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { Account, User } from '@mitel/cloudlink-sdk';
import { AppInjector } from '../../app-injector';
import { AdminService } from './admin.service';
import { AccountsService } from './accounts.service';
import { RBACAuthGuardService } from './../guards/rbac-auth-guard.service';
import { TenantsService } from './tenants.service';
import { IntegUnifyPhoneSharedService } from './integ-unify-phone-shared.service';
import { OrdersService } from './orders.service';
import { SpinnerService } from './spinner.service';
import { IntegrationItemServiceInf, StatusString, UserLicense, IntegrationProductFilterInf, IntegrationAsyncOperationInf } from '../interfaces/integrations';
import { UnifyPhoneDialogComponent } from './../../modules/integrations-applications/components/integ-config-dialogs/unify-phone-dialog/unify-phone-dialog.component';
import { ConfirmationDialogs } from './../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { ConfirmationDialogComponent } from './../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { StatusType } from '../constants/statusType.enum';
import { Tenant, TenantType } from './../interfaces/tenants';
import { productCodes, unifyPhoneCodes } from './../constants/constants';
import { ActionType, CategoryId } from '../utils/rbac.util';

const logContext = 'IntegUnifyPhoneService';
@Injectable({
  providedIn: 'root'
})
export class IntegUnifyPhoneService implements IntegrationItemServiceInf, IntegrationProductFilterInf, IntegrationAsyncOperationInf {
  private adminService: AdminService;
  private accountsService: AccountsService;
  private rbacAuthGuardService: RBACAuthGuardService;
  private tenantsService: TenantsService;
  private integUnifyPhoneSharedService: IntegUnifyPhoneSharedService;
  private ordersService: OrdersService;
  private translateService: TranslateService;
  private spinnerService: SpinnerService;
  public dialog: MatDialog;

  constructor() { }

  initServices() {
    if (!this.adminService)
      this.adminService = AppInjector.injector.get(AdminService);

    if (!this.accountsService)
      this.accountsService = AppInjector.injector.get(AccountsService);

    if (!this.rbacAuthGuardService)
      this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);

    if (!this.tenantsService)
      this.tenantsService = AppInjector.injector.get(TenantsService);

    if (!this.integUnifyPhoneSharedService)
      this.integUnifyPhoneSharedService = AppInjector.injector.get(IntegUnifyPhoneSharedService);

    if (!this.ordersService)
      this.ordersService = AppInjector.injector.get(OrdersService);

    if (!this.translateService)
      this.translateService = AppInjector.injector.get(TranslateService);

    if (!this.spinnerService)
      this.spinnerService = AppInjector.injector.get(SpinnerService);

    if (!this.dialog)
      this.dialog = AppInjector.injector.get(MatDialog);   
  }

  async canBeSelected(account: Account): Promise<boolean> {
    this.initServices();

    let isDenied = this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.INTEGRATIONUNIFYPHONE, ActionType.VIEW);
    return !isDenied && !Boolean(await this.isAsyncInProgress(account));
  }

  getConfigurationDialog(): any {
    return UnifyPhoneDialogComponent;
  }

  canHaveAsyncOperations(account: Account): Promise<boolean> {
    return Promise.resolve(true);
  }

  async isAsyncInProgress(account: Account): Promise<boolean> {
    this.initServices();

    try {
      const tags = await this.adminService.getAccountTags(account.accountId);
      return tags?.UnifyPhone?.async?.asyncDeleteJobId != undefined;
    } catch (error) {
      console.error(`${logContext}.isAsyncInProgress - An error occurred while trying to determine async state for Unify Phone: `, error);
      return false;
    }
  }

  async isDeleteAsync(account: Account): Promise<boolean> {
    this.initServices();
    return await this.isSetupComplete(account);
  }

  async getAsyncJobIdsInProgress(account: Account): Promise<string[]> {
    this.initServices();

    try {
      const tags = await this.adminService.getAccountTags(account.accountId);
      // there is only 1 possible async job we are concerned with
      const asyncDeleteJobId = tags?.UnifyPhone?.async?.asyncDeleteJobId;
      return [...(asyncDeleteJobId ? [asyncDeleteJobId] : [])];
    } catch (error) {
      console.error(`${logContext}.getAsyncJobIdsInProgress - An error occurred while trying to determine the async state for Unify Phone:`, error);
      return [];
    }
  }

  async asyncJobIsComplete(account: Account, jobId: string) {
    this.initServices();

    try {
      const newAccount = await this.adminService.getAccountWithoutFrills(account.accountId, null, { query: { $expand: 'tags' } });

      if (newAccount?.tags?.UnifyPhone?.async?.asyncDeleteJobId != jobId) {
        return; // nothing to do. This really should not happen, testing incase of a glare situation
      }
      // at this point the only async operation that is integration affecting is delete
      // continue with the delete
      await this.deleteIntegrationSync(newAccount, true);
    } catch (error) {
      console.error(`${logContext}.asyncJobIsComplete - An error occurred while trying to determine the Unify Phone account: `, error);
    }
  }

  async deleteIntegrationSync(account: Account, removeProductTag: boolean): Promise<boolean> {
    this.initServices();

    try {
      if (removeProductTag) {
        let pos = account?.tags?.products?.indexOf('UnifyPhone');
        if (pos > -1) {
          let dataChanges = {
            tags: account.tags
          };

          dataChanges.tags.products.splice(pos, 1);
          let updatedAccount = await this.adminService.updateAccount(account.accountId, dataChanges);
          this.accountsService.mergeAccountWithModel(updatedAccount);
        }
      }
    }
    catch (error) {
      console.error(`${logContext}.deleteIntegrationSync - An error occurred while trying to update the Unify Phone account: `, error);
    }

    try {
      if (account?.tags?.UnifyPhone) { // just in case there is a tag without a tenant id
        await this.adminService.deleteAccountTag(account.accountId, productCodes.UNIFY_PHONE);
      }
    }
    catch (error) {
      console.error(`${logContext}.deleteIntegrationSync - An error occurred while trying to delete the Unify Phone tags:`, error);
    }

    this.integUnifyPhoneSharedService.deleteSIPTrunksFromCache(account);
    this.integUnifyPhoneSharedService.deleteTenantFromCache(account);
    return true;
  }
  
  async asyncJobFailed(account: Account, jobId: string, reason: any) {
    this.initServices();

    try {
      console.log(`${logContext}.asyncJobFailed - The async job ${jobId} failed: `, reason);
      const newAccount = await this.adminService.getAccountWithoutFrills(account.accountId, null, { query: { $expand: 'tags' } });

      let tags = newAccount?.tags;
      if (tags?.UnifyPhone?.async?.asyncDeleteJobId != jobId) {
        return; // nothing to do. This really should not happen, testing incase of a glare situation
      }
      // at this point the only async operation that is integration affecting is delete
      // continue with the delete

      if (tags?.UnifyPhone?.async?.asyncDeleteJobId) { // just in case there is a tag without a tenant id
        let dataChanges = {
          tags: tags
        };
        dataChanges.tags.UnifyPhone.asyncDeleteError = reason;
        
        try {
          let updatedAccount = await this.adminService.updateAccount(account.accountId, dataChanges);
          this.accountsService.mergeAccountWithModel(updatedAccount);
        }
        catch (error) {
          console.error(`${logContext}.asyncJobFailed - An error occurred while trying to update the Unify Phone account: `, error);
        }

        try {
          await this.adminService.deleteAccountTag(account.accountId, productCodes.UNIFY_PHONE + '.async');
        }
        catch (error) {
          console.error(`${logContext}.asyncJobFailed - An error occurred while trying to delete the Unify Phone tags:`, error);
        }
          
        this.integUnifyPhoneSharedService.deleteSIPTrunksFromCache(account);
        this.integUnifyPhoneSharedService.deleteTenantFromCache(account);
      }
    }
    catch (error) {
      console.error(`${logContext}.asyncJobFailed - An error occurred while handling a failed deletion: `, error);
    }
  }

  async isSetupComplete(account: Account): Promise<boolean> {
    this.initServices();

    try {
      const tags = await this.adminService.getAccountTagsNoFrills(account.accountId);
      return tags?.UnifyPhone?.setupComplete == true;
    } catch (error) {
      console.error(`${logContext}.isSetupComplete - Error determining the account tags: `, error);
      return false;
    }
  }

  async getUnifyPhoneTenant(account: Account): Promise<Tenant | undefined> {
    this.initServices();
    this.spinnerService.setSpinnerMessageByKey('app.loading');
    this.spinnerService.showSpinner();

    try {
      const tenant = await this.integUnifyPhoneSharedService.getTenant(account);
      this.spinnerService.hideSpinner();
      return tenant;
    } catch (error) {
      console.error(`${logContext}.getUnifyPhoneTenant - An error ocurred while getting Unify Phone tenant fails: `, error);
      this.spinnerService.hideSpinner();
      throw error;
    };
  }

  async handleIntegrationStateChange(account: Account, state: boolean): Promise<any> {
    this.initServices();

    this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
    this.spinnerService.showSpinner();

    try {
      if (state == false) {
        const newAccount = await this.adminService.getAccountWithoutFrills(account.accountId, null, { query: { $expand: 'tags' } });

        try {
          const tenant = await this.integUnifyPhoneSharedService.getTenant(account);
          if (tenant) {
            // this means setup was complete
            const result = await this.tenantsService.deleteTenant(tenant.tenantId, tenant, '?mode=async');

            // we have an async job id, save it in the tags
            let dataChanges = {
              tags: newAccount.tags
            };

            dataChanges.tags.UnifyPhone.async = {
              asyncDeleteJobId: result?.jobId,
              asyncDateTime: new Date().getTime()
            };

            try {
              const updatedAccount = await this.adminService.updateAccount(account.accountId, dataChanges);
              this.accountsService.mergeAccountWithModel(updatedAccount);
            }
            catch (error) {
              console.error(`${logContext}.handleIntegrationStateChange - An error occurred while trying to update the Unify Phone account: `, error);
            }

            try {
              if (newAccount?.tags?.UnifyPhone?.asyncDeleteError) {
                await this.adminService.deleteAccountTag(account.accountId, productCodes.UNIFY_PHONE + '.asyncDeleteError');
              }
            }
            catch (error) {
              console.error(`${logContext}.handleIntegrationStateChange - An error occurred while trying to delete the Unify Phone tags:`, error);
            }
            
            return true;
          } else {
            // sync delete - no tenant exists
            return await this.deleteIntegrationSync(newAccount, false);
          }
        } catch (error) {
          console.error(`${logContext}.handleIntegrationStateChange - An error occurred while updating the Unify Phone tags:`, error); this.spinnerService.hideSpinner();
          throw error;
        }
      } else  {
        // it appears old cache values are sticking around if there is an async delete followed immediately by an add
        // depending on the user actions. So we will clear the cache on an add just to have a clean state
        this.integUnifyPhoneSharedService.deleteSIPTrunksFromCache(account);
        this.integUnifyPhoneSharedService.deleteTenantFromCache(account);
      }
    } catch (error) {
      console.error(`${logContext}.handleIntegrationStateChange - An error occurred while trying to delete the Unify Phone integration:`, error); this.spinnerService.hideSpinner();
      throw error;
    } finally {
      this.spinnerService.hideSpinner();
    }
  }

  async getStatusString(account: Account): Promise<StatusString> {
    this.initServices();

    let integStatus: StatusString;

    try {
      let tags = await this.adminService.getAccountTagsNoFrills(account.accountId);
      let isSetupComplete = tags?.UnifyPhone?.setupComplete;
      if (isSetupComplete == true) {
        if (tags?.UnifyPhone?.asyncDeleteError) {
          // there was a delete failure
          integStatus = {
            status: this.translateService.instant('integrations.unify-phone.delete-failed') + ': ' + tags.UnifyPhone.asyncDeleteError,
            type: StatusType.error
          };
        } else if (tags?.UnifyPhone?.async?.asyncDeleteJobId) {
          // there is an async job in progress so that should be the error displayed
          let dateStr = new Date(tags.UnifyPhone.async.asyncDateTime).toLocaleString();
          integStatus = {
            status: this.translateService.instant('integrations.unify-phone.delete-in-progress') + dateStr,
            type: StatusType.error
          };
        } else if (tags?.SIPTrunks?.setupComplete == true) {
          // check for SIP trunks
          try {
            const sipTrunks = await this.integUnifyPhoneSharedService.getSIPTrunks(account);
            if (sipTrunks?.length > 0) {
              const plural = sipTrunks?.length > 1 ? 's' : '';
              integStatus = {
                status: this.translateService.instant(`integrations.unify-phone.sip-trunk${plural}-total-message`, { numSIPTrunks: sipTrunks?.length }),
                type: StatusType.normal
              };
            } else {
              integStatus = {
                status: 'integrations.unify-phone.no-sip-trunks-message',
                type: StatusType.caution
              };
            }
          } catch (error) {
            // tenant is missing somehow, not expected
            if (error == 'Missing tenant for account') {
              integStatus = {
                status: 'integrations.unify-phone.no-tenant-message',
                type: StatusType.caution
              };
            } else {
              throw error;
            } 
          }
        } else {
          const canConfigure = await this.integUnifyPhoneSharedService.canConfigureSIPTrunks(account);
          if (canConfigure) {
            integStatus = {
              status: 'integrations.unify-phone.no-sip-connectivity-configuration',
              type: StatusType.caution
            };
          } else {
            integStatus = {
              status: 'integrations.unify-phone.missing-pbx-account',
              type: StatusType.caution
            };
          }
        }
      } else {
        integStatus = {
          status: 'integrations.unify-phone.no-config-message',
          type: StatusType.caution
        };
      }
      return integStatus;
    } catch (error) {
      console.error(`${logContext}.getStatusString - Failed to determine the status: `, error);
      return undefined;
    };
  }

  getConfigButtonLabel(account: Account): Promise<string> {
    return Promise.resolve(undefined);
  }

  isButtonDisabled(account: Account): Promise<boolean> {
    return Promise.resolve(false);
  }

  async saveConfigurationData(account: Account, unifyPhoneTenant: any): Promise<boolean> {
    this.initServices();

    try {
      this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
      this.spinnerService.showSpinner();

      const tenant = await this.tenantsService.createTenant({
        tenantType: TenantType.UNIFY_PHONE, 
        unifyPhoneTenant: unifyPhoneTenant
      });

      const dataChanges: any = {
        tags: {
          UnifyPhone: {
            setupComplete: true
          }
        }
      };

      const updatedAccount = await this.adminService.updateAccount(account.accountId, dataChanges);
      this.accountsService.mergeAccountWithModel(updatedAccount);

      // add new tenant to cache
      this.integUnifyPhoneSharedService.addTenantToCache(account, tenant);

      return true;
    } catch (error) {
      console.error(`${logContext}.saveConfigurationData - Failed to save Unify Phone integration: `, error);
      throw error;
    } finally {
      this.spinnerService.hideSpinner();
    }
  }

  async canDeleteIntegration(account: Account): Promise<boolean> {
    this.initServices();

    return await firstValueFrom(this.dialog.open(ConfirmationDialogComponent, {
      data: { mode: ConfirmationDialogs.REMOVE_UNIFY_PHONE_CONFIRMATION }
    }
    ).afterClosed());
  }

  canFilterUsersOnProduct?(account: Account): boolean {
    return true;
  }

  async filterUsersBasedOnProduct(account: Account, users: User[], filter: any[]): Promise<User[]> {
    this.initServices();
    let newUsersList = new Array<User>();

    for (var i = 0; i < users.length; i++) {
      const license = await this.getUserLicense(users[i], account);
      if (license) {
        newUsersList.push(users[i]);
      }
    }
    return newUsersList;
  }
  
  async getUserLicense(user: User, account: Account): Promise<UserLicense> {
    this.initServices();

    const licenses = await this.ordersService.getUsersLicensesFromCachedList(user.userId);
    let userLicense: UserLicense;

    // if there is a legit subscription license
    for (var i = 0; i < licenses.length; i++) {
      if (unifyPhoneCodes.USER_PRODUCT_NAME === licenses[i]) {
        userLicense = {
          code: unifyPhoneCodes.USER_PRODUCT,
          displayStringKey: 'product_licenses.unifyPhone.name'
        }
        return userLicense;
      }
    }
    return undefined;
  }
  
}
